import { Box, Button, Grid, Stack, TextField, Typography } from '@mui/material'
import React, { memo, useEffect, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { TFinishLine } from '../../shared/types/finishLine.type'
import { t } from 'i18next'
import { CompareArrows, Delete } from '@mui/icons-material'
import { useTracks } from '../../hooks/useTracks'
import { useAuth } from '../../hooks/useAuth'
import { LoadingButton } from '@mui/lab'
import { debounce, get, isEmpty, set, toNumber } from 'lodash'
import { toast } from 'react-toastify'
import PlaceAutoComplete from './PlaceAutoComplete'
type FinishLineFormType = {
    onFlSaved: (item: TFinishLine, newAdress: string | null) => void,
    onLatsChanged: ({ latitude_a, latitude_b, longitude_a, longitude_b }: Partial<TFinishLine>) => void,
    lats: Partial<TFinishLine> | null,
    deleteFinishLine: () => void,
    initLats: () => void,
    deleting?: boolean,
    finishLine?: TFinishLine | null,
    adress: string | null,
    onAdressChange: (adress: string | null) => void
}

const FLOAT = "any"

function FinishLineForm({ onAdressChange, adress, deleting, onFlSaved, onLatsChanged, lats, deleteFinishLine, initLats, finishLine }: FinishLineFormType) {

    const { saveFL, isSavingFL } = useTracks(false)
    const { auth } = useAuth()
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, touchedFields },

    } = useForm<Partial<TFinishLine>>()

    useEffect(() => {
        if (!lats) return

        setValue('latitude_a', lats.latitude_a)
        setValue('latitude_b', lats.latitude_b)
        setValue('longitude_a', lats.longitude_a)
        setValue('longitude_b', lats.longitude_b)
    }, [JSON.stringify(lats)])


    const getDistance = useMemo(() => {
        return google.maps.geometry.spherical.computeDistanceBetween({ lat: toNumber(lats?.latitude_a), lng: toNumber(lats?.longitude_a) }, { lat: toNumber(lats?.latitude_b), lng: toNumber(lats?.longitude_b) })
    }, [lats])



    ////Watch fields to update map
    const watchFields = watch()
    const debounced = debounce((fields) => !isEmpty(fields) && onLatsChanged(formatToNumbers(fields)), 400)
    useEffect(() => {
        debounced(watchFields)
        return () => debounced.cancel()
    }, [JSON.stringify(watchFields)])


    const formatToNumbers = (data: Partial<TFinishLine>) => {
        let formatted = {}

        for (const [key, value] of Object.entries(data)) {
            set(formatted, key, toNumber(value))
        }
        return formatted
    }

    const relocateAdress = async (lat?: number, lng?: number) => {
        if (!lat || !lng) return null
        try {
            const geocoder = new window.google.maps.Geocoder();
            let response = await geocoder.geocode({ location: { lat, lng } })
            if (response && !isEmpty(response.results)) {
                return response.results[0].formatted_address
            }
            return null
        } catch (error) {
            return null
        }
    }

    const onSubmit: SubmitHandler<Partial<TFinishLine>> = (data) => {

        let formatted: Partial<TFinishLine> = { ...formatToNumbers(data), trainer: get(auth, '@id') }

        saveFL(formatted).then(
            async (result) => {
                if (result) {
                    let newAdress = await relocateAdress(data.latitude_a, data.longitude_a)
                    if (newAdress) {
                        onAdressChange(newAdress)
                    }
                    onFlSaved(result, newAdress)
                }
            }
        ).catch(err => {
            toast(t('error:generic'), { type: 'error' })
        })
    }


    return (
        <Stack direction={'column'} spacing={2} my={2}>
            <PlaceAutoComplete defaultValue={adress} onValueChange={onAdressChange} />
            {
                !lats ?
                    <Box display={'flex'} justifyContent={'end'} >
                        <Button variant='outlined' onClick={initLats}>{t('tracks:addFinishLine')}</Button>
                    </Box>
                    : <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems={'center'} >
                            <Grid item md={5} xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item md={6} xs={12}>
                                        <TextField fullWidth inputProps={{
                                            step: FLOAT
                                        }} type='number' label={t('tracks:latitudeA')}  {...register('latitude_a')}></TextField>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <TextField fullWidth inputProps={{
                                            step: FLOAT
                                        }} type='number' label={t('tracks:longitudeA')}  {...register('longitude_a')}></TextField>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item md={2} xs={12}>
                                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} flex={1}>
                                    <CompareArrows sx={{ color: 'gray' }}></CompareArrows>
                                    <Typography variant='caption' color={'GrayText'}>{getDistance.toFixed(3)}  m</Typography>
                                </Box>
                            </Grid>

                            <Grid item md={5} xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item md={6} xs={12}>
                                        <TextField fullWidth inputProps={{
                                            step: FLOAT
                                        }} type='number' label={t('tracks:latitudeB')}  {...register('latitude_b')}></TextField>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <TextField fullWidth inputProps={{
                                            step: FLOAT
                                        }} type='number' label={t('tracks:longitudeB')}  {...register('longitude_b')}></TextField>
                                    </Grid>
                                </Grid>
                            </Grid>


                        </Grid>
                        <Box display={'flex'} justifyContent={'end'} my={2} gap={1}>
                            <LoadingButton loading={deleting} onClick={deleteFinishLine} variant='outlined' color='error' startIcon={<Delete></Delete>}>{t('tracks:deleteFinishLine')}</LoadingButton>
                            <LoadingButton loading={isSavingFL} variant='contained' type='submit'>{t('button:save')}</LoadingButton>
                        </Box>
                    </form>
            }


        </Stack>

    )
}

export default memo(FinishLineForm)